import { useDisclosure } from '@chakra-ui/react'
import type React from 'react'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import Invoice01Icon from '../../../../../assets/invoice-01-stroke-rounded'
import PageWrapper from '../../../../../components/Pagewrapper'
import Table from '../../../../../components/Table/Table'
import { useGetKrakenClosedOrdersQuery } from '../../../../../redux/services/coreApi'
import { setBookkeepingTableState } from '../../../../../redux/slices/tableFiltersSlice'
import { RootState } from '../../../../../redux/store'
import type { KrakenClosedOrder } from '../../../../../types/coreApi-types'
import AdminGenerateInvoiceModal from './components/AdminGenerateInvoiceModal'

type BookkeepingScreenProps = {}

const BookkeepingScreen: React.FC<BookkeepingScreenProps> = () => {
  const bookkeepingTableState = useSelector(
    (state: RootState) => state.bookkeepingTable
  )

  const { data: closedOrdersData, isLoading } = useGetKrakenClosedOrdersQuery({
    start: undefined,
    end: undefined,
  })

  const generateInvoiceModal = useDisclosure()

  // Convert the orders Record to an array for the table
  const ordersArray = useMemo(() => {
    if (!closedOrdersData?.orders) return []

    return Object.entries(closedOrdersData.orders).map(([orderId, order]) => ({
      id: orderId,
      ...order,
      // Flatten description fields for easier access
      pair: order.description?.pair || null,
      type: order.description?.type || null,
    }))
  }, [closedOrdersData])

  // Format timestamp to readable date
  const formatTimestamp = (timestamp: number | null | undefined): string => {
    if (!timestamp) return '-'
    return new Date(timestamp * 1000).toLocaleString()
  }

  // Format number string to readable number
  const formatNumber = (value: string | null | undefined): string => {
    if (!value) return '-'
    const num = parseFloat(value)
    if (isNaN(num)) return value
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    })
  }

  return (
    <PageWrapper title="Bookkeeping - Kraken Closed Orders">
      <div className="mb-4 flex justify-end">
        <button
          className="button flex items-center gap-2"
          onClick={generateInvoiceModal.onOpen}
        >
          <Invoice01Icon width={18} height={18} className="text-white" />
          Generate Swap Invoice
        </button>
      </div>

      <Table
        tableState={bookkeepingTableState}
        setTableState={setBookkeepingTableState}
        navigate={() => null}
        onClickRow={() => {}}
        isLoading={isLoading}
        emptyDataRender={
          <div className="text-xl mb-3">No closed orders found.</div>
        }
        defaultData={ordersArray}
        schema={[
          {
            header: 'Timestamp',
            accessorKey: 'closeTime',
            type: 'date',
            priority: 1,
            cellValueWrapper: (value, originalValue, { original }) => {
              const order = original as KrakenClosedOrder & { id: string }
              return (
                <div className="px-2 py-1">
                  {formatTimestamp(order.closeTime)}
                </div>
              )
            },
          },
          {
            header: 'Pair',
            accessorKey: 'pair',
            type: 'string',
            priority: 2,
            cellValueWrapper: (value, originalValue, { original }) => {
              const order = original as KrakenClosedOrder & {
                id: string
                pair?: string | null
              }
              return (
                <div className="px-2 py-1">
                  {order.pair || order.description?.pair || '-'}
                </div>
              )
            },
          },
          {
            header: 'Type',
            accessorKey: 'type',
            type: 'string',
            priority: 3,
            cellValueWrapper: (value, originalValue, { original }) => {
              const order = original as KrakenClosedOrder & {
                id: string
                type?: string | null
              }
              return (
                <div className="px-2 py-1">
                  {order.type || order.description?.type || '-'}
                </div>
              )
            },
          },
          {
            header: 'Volume (base currency)',
            accessorKey: 'volume',
            type: 'string',
            priority: 4,
            cellValueWrapper: (value, originalValue, { original }) => {
              const order = original as KrakenClosedOrder & { id: string }
              return (
                <div className="px-2 py-1">{formatNumber(order.volume)}</div>
              )
            },
          },
          {
            header: 'Fee (quote currency)',
            accessorKey: 'fee',
            type: 'string',
            priority: 5,
            cellValueWrapper: (value, originalValue, { original }) => {
              const order = original as KrakenClosedOrder & { id: string }
              return <div className="px-2 py-1">{formatNumber(order.fee)}</div>
            },
          },
          {
            header: 'Price (quote currency)',
            accessorKey: 'price',
            type: 'string',
            priority: 6,
            cellValueWrapper: (value, originalValue, { original }) => {
              const order = original as KrakenClosedOrder & { id: string }
              return (
                <div className="px-2 py-1">{formatNumber(order.price)}</div>
              )
            },
          },
        ]}
      />

      <AdminGenerateInvoiceModal
        isOpen={generateInvoiceModal.isOpen}
        onClose={generateInvoiceModal.onClose}
      />
    </PageWrapper>
  )
}

export default BookkeepingScreen
